import { MoreVertical, Edit, Pause, Play, Trash2, Radio, Clock, RefreshCw, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { DropdownMenu } from '../ui/DropdownMenu';
import { streamLeafCardSurface } from '../../lib/colors';
import { cn } from '../../lib/utils';
import { RoleScopeChips, type StreamDefinitionLabelMaps } from './RoleScopeChips';

export interface RoleSummary {
  streamId: string;
  streamDisplayName: string;
  code?: string;
  products?: string[];
  teams?: string[];
  tenants?: string[];
  /** @deprecated Legacy field from nested model */
  product?: string;
  /** @deprecated Legacy field from nested model */
  productDisplayName?: string;
  /** @deprecated Legacy field from nested model */
  teamType?: string;
  /** @deprecated Legacy field from nested model */
  tenantCode?: string;
  /** @deprecated Legacy field from nested model */
  tenantDisplayName?: string;
  agentTitle?: string;
  agentDescription?: string;
  parentRef?: string;
  enabled: boolean;
  roleEnabled?: boolean;
  streamEnabled?: boolean;
  effectiveEnabled?: boolean;
  adoProject: string;
  adoAreaPath?: string;
  repo: string;
  botDisplayName: string;
  workflowTypeCount: number;
  /** From stream YAML `color` (hex or CSS color). */
  streamColor?: string;
  syncState?: {
    status: 'in_sync' | 'pending_sync' | 'syncing' | 'error';
    dirtyReasons?: string[];
    lastPrUrl?: string;
    lastError?: string;
    updatedAt?: string;
  };
}

interface RoleConfigCardProps {
  role: RoleSummary;
  /** When false, stream YAML `color` is ignored (parent roles that have child roles in the org chart). */
  applyStreamLeafColor?: boolean;
  definitionLabelMaps?: StreamDefinitionLabelMaps;
  canManage: boolean;
  canChat?: boolean;
  onEditRole: (role: RoleSummary) => void;
  onPauseToggle: (role: RoleSummary) => void;
  onDelete: (role: RoleSummary) => void;
  onListeners?: (role: RoleSummary) => void;
  onSchedules?: (role: RoleSummary) => void;
  onReconcile?: (role: RoleSummary) => void;
  onChatRole?: (role: RoleSummary) => void;
  onSyncRole?: (role: RoleSummary) => void;
  syncing?: boolean;
}

export function RoleConfigCard({
  role,
  applyStreamLeafColor = true,
  definitionLabelMaps,
  canManage,
  canChat,
  onEditRole,
  onPauseToggle,
  onDelete,
  onListeners,
  onSchedules,
  onReconcile,
  onChatRole,
  onSyncRole,
  syncing = false,
}: RoleConfigCardProps) {
  const surf = streamLeafCardSurface(
    applyStreamLeafColor ? role.streamColor : undefined,
    'neutral',
  );
  const roleEnabled = role.roleEnabled ?? role.enabled;
  const streamEnabled = role.streamEnabled ?? true;
  const syncStatus = role.syncState?.status || 'pending_sync';
  const syncBadgeVariant =
    syncStatus === 'in_sync'
      ? 'success'
      : syncStatus === 'error'
        ? 'destructive'
        : syncStatus === 'syncing'
          ? 'secondary'
          : 'warning';
  return (
    <Card
      className={cn('h-full flex flex-col min-w-0', surf.className)}
      style={surf.style}
    >
      <CardHeader className="pb-2 pt-4 px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1 space-y-1">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 break-words whitespace-normal leading-snug">
              {role.agentTitle || role.tenantDisplayName || role.code}
            </h3>
            {role.agentDescription && (
              <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">{role.agentDescription}</p>
            )}
            <RoleScopeChips role={role} definitionLabelMaps={definitionLabelMaps} density="grid" className="mt-1" />
          </div>
          <div className="flex items-start justify-end gap-0.5 shrink-0">
            {canChat && onChatRole && role.code ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 shrink-0"
                aria-label="Chat with role"
                onClick={() => onChatRole(role)}
              >
                <MessageSquare className="w-4 h-4" />
              </Button>
            ) : null}
            {canManage ? (
              <DropdownMenu
                align="end"
                trigger={
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 shrink-0" aria-label="Role actions">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                }
                items={[
                  ...(canChat && onChatRole && role.code
                    ? [
                        {
                          label: 'Chat with role',
                          icon: <MessageSquare className="w-4 h-4" />,
                          onClick: () => onChatRole(role),
                        },
                      ]
                    : []),
                  {
                    label: 'Edit role config',
                    icon: <Edit className="w-4 h-4" />,
                    onClick: () => onEditRole(role),
                  },
                  ...(onListeners ? [{
                    label: 'Configure Listeners',
                    icon: <Radio className="w-4 h-4" />,
                    onClick: () => onListeners(role),
                  }] : []),
                  ...(onSchedules ? [{
                    label: 'Scheduled jobs',
                    icon: <Clock className="w-4 h-4" />,
                    onClick: () => onSchedules(role),
                  }] : []),
                  ...(onReconcile ? [{
                    label: 'Reconcile resources',
                    icon: <RefreshCw className="w-4 h-4" />,
                    onClick: () => onReconcile(role),
                  }] : []),
                  {
                    label: roleEnabled ? 'Pause Role' : 'Resume Role',
                    icon: roleEnabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />,
                    onClick: () => onPauseToggle(role),
                  },
                  {
                    label: 'Delete',
                    icon: <Trash2 className="w-4 h-4" />,
                    onClick: () => onDelete(role),
                    className: 'text-red-600 dark:text-red-400',
                  },
                ]}
              />
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4 pt-0 flex-1 flex flex-col gap-3">
        <div className="flex flex-wrap gap-1.5">
          <Badge variant={roleEnabled ? 'success' : 'warning'}>
            {roleEnabled ? 'Role active' : 'Role paused'}
          </Badge>
          {!streamEnabled ? <Badge variant="warning">Stream paused</Badge> : null}
          <Badge variant={syncBadgeVariant}>{syncStatus.replace('_', ' ')}</Badge>
          {role.code && (
            <Badge variant="outline" className="text-[10px] font-mono">{role.code}</Badge>
          )}
        </div>
        {onSyncRole ? (
          <Button
            type="button"
            variant="outline"
            className="h-8"
            disabled={syncing || syncStatus === 'syncing'}
            onClick={() => onSyncRole(role)}
          >
            {syncing || syncStatus === 'syncing' ? 'Syncing…' : 'Sync role'}
          </Button>
        ) : null}

        <dl className="grid grid-cols-1 gap-1.5 text-xs text-gray-600 dark:text-gray-400 mt-auto">
          <div className="flex justify-between gap-2">
            <dt className="text-gray-500 dark:text-gray-500 truncate">ADO Project</dt>
            <dd className="font-medium text-gray-800 dark:text-gray-200 truncate text-right" title={role.adoProject}>
              {role.adoProject}
            </dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-gray-500 dark:text-gray-500 truncate">Repository</dt>
            <dd className="font-medium text-gray-800 dark:text-gray-200 truncate text-right" title={role.repo}>
              {role.repo}
            </dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-gray-500 dark:text-gray-500 truncate">Bot</dt>
            <dd className="font-medium text-gray-800 dark:text-gray-200 truncate text-right" title={role.botDisplayName}>
              {role.botDisplayName || '—'}
            </dd>
          </div>
          <div className="flex justify-between gap-2">
            <dt className="text-gray-500 dark:text-gray-500">Workflow types</dt>
            <dd className="font-medium text-gray-800 dark:text-gray-200">{role.workflowTypeCount}</dd>
          </div>
        </dl>
      </CardContent>
    </Card>
  );
}
